import { useEffect, useState } from "react";

declare global {
    export interface Window {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        MktoForms2?: any;
    }
}

export type MarketoIds = {
    readonly munchkinId: string;
    readonly munchkinIdStg: string;
    readonly formId: number;
    readonly formIdStg: number;
    readonly businessEmailValidation?: string;
};

export type MarketoConfig = {
    readonly url: string;
    readonly munchkinId: string;
    readonly prefsUrl: string;
    readonly formId: number;
};

export function useMarketoForm(
    {
        munchkinId,
        munchkinIdStg,
        formId,
        formIdStg,
        businessEmailValidation
    }: MarketoIds,
    container: HTMLDivElement | null
) {
    const [marketo, setMarketo] = useState<Window["MktoForms2"]>();
    const [status, setStatus] = useState<
        "LOADING" | "FORM" | "SUCCESS" | "ERROR"
    >("LOADING");
    const setFormStatus = () => setStatus("FORM");
    const setSuccessStatus = () => setStatus("SUCCESS");
    const setErrorStatus = () => setStatus("ERROR");

    useEffect(() => {
        // Only run in browser environment
        if (typeof window === "undefined" || typeof document === "undefined") {
            return;
        }

        const mktoConfig: MarketoConfig =
            location.hostname === "trello.com" ||
            location.hostname === "www.trello.com"
                ? {
                      url: "https://pages.eml.atlassian.com",
                      munchkinId: munchkinId,
                      prefsUrl:
                          "https://preferences.atlassian.com/rest/forms/config",
                      formId: formId
                  }
                : {
                      url: "https://app-ab35.marketo.com",
                      munchkinId: munchkinIdStg,
                      prefsUrl:
                          "https://preferences.stg.atlassian.com/rest/forms/config",
                      formId: formIdStg
                  };
        const scriptId = `marketo-${mktoConfig.munchkinId}`;
        const script = document.getElementById(scriptId);

        const waitForMarketo = (scriptElement: HTMLElement) => {
            function stopWaiting() {
                scriptElement.removeEventListener("load", updateMarketo);
                window.clearInterval(pollId);
            }

            function updateMarketo() {
                if (window.MktoForms2) {
                    setMarketo(window.MktoForms2);
                    stopWaiting();
                }
            }

            // Another form can create the shared script before MktoForms2 exists.
            scriptElement.addEventListener("load", updateMarketo);
            const pollId = window.setInterval(updateMarketo, 250);
            updateMarketo();

            return stopWaiting;
        };

        if (!marketo) {
            if (script) {
                return waitForMarketo(script);
            } else {
                const script = document.createElement("script");
                script.async = true;
                script.src = `${mktoConfig.url}/js/forms2/js/forms2.min.js`;
                script.id = scriptId;

                document.body.appendChild(script);

                return waitForMarketo(script);
            }
        } else if (status === "LOADING" && container) {
            loadMarketoForm(
                marketo,
                mktoConfig,
                container,
                businessEmailValidation,
                setFormStatus,
                setSuccessStatus,
                setErrorStatus
            );
        }
    }, [
        marketo,
        status,
        container,
        munchkinId,
        munchkinIdStg,
        formId,
        formIdStg,
        businessEmailValidation
    ]);

    return status;
}

function loadMarketoForm(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    marketo: any,
    mktoConfig: MarketoConfig,
    container: HTMLDivElement | null,
    businessEmailValidation: string | undefined, // this is an error validation messages
    setFormStatus: () => void,
    setSuccessStatus: () => void,
    setErrorStatus: () => void
) {
    const { $, loadForm } = marketo;
    loadForm(
        mktoConfig.url,
        mktoConfig.munchkinId,
        mktoConfig.formId,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (form: any) => {
            if (!form) {
                setErrorStatus();
                return;
            }
            const $container = $(container);

            $container.hide();
            $container.append(form.render());

            const $form = $container.find("form");

            //overwrite form ID based on config
            $form.attr("id", `foo-bar-mktoForm_${mktoConfig.formId}`);

            //remove the elements we don't want
            $(
                "#mktoForms2BaseStyle, #mktoForms2ThemeStyle, .mktoForm style, .mktoGutter, .mktoClear, .mktoOffset"
            ).remove();

            //remove style attributes from form and child elements
            $form.removeAttr("style");
            $form.find("*").removeAttr("style");

            //populate hidden date fields
            const optinDate = new Date().toISOString();
            $form.find("input[name=form_anyConsentUpdatedAt]").val(optinDate);
            $form.find("input[name=form_gmoiUpdatedAt]").val(optinDate);
            $form
                .find("input[name=form_privacyPolicyUpdatedAt]")
                .val(optinDate);

            //new updated at
            const $addSubs = $form.find(
                "input[name=form_additionalSubscriptions]"
            );
            const $addSubsUpdated = $form.find(
                "input[name=form_additionalSubscriptionsUpdatedAt]"
            );

            if (
                $addSubs.length &&
                $addSubs.first().val().length &&
                $addSubsUpdated.length
            ) {
                $addSubsUpdated.first().val(optinDate);
            }

            //populate hidden url fields
            const pageUrl = window.location.href;
            $form.find("input[name=form_gmoiFormUrl]").val(pageUrl);
            $form.find("input[name=form_privacyPolicyFormUrl]").val(pageUrl);

            //option to set the submit button text from the html
            const submitText = $form.attr("data-submit");
            if (submitText) {
                $form.find("button").text(submitText);
            }

            form.onSubmit(function () {
                // Pull utms from localStorage
                try {
                    const marketingContext =
                        localStorage.getItem("marketingContext");
                    if (marketingContext) {
                        const parsedMarketingContext =
                            JSON.parse(marketingContext);
                        const utms = parsedMarketingContext.utm;

                        if ($form.find("input[name=UTM_SFDC_Campaign_ID__c]"))
                            $form
                                .find("input[name=UTM_SFDC_Campaign_ID__c]")
                                .val(utms.sfdcCampaignId);
                        if ($form.find("input[name=utmSource]"))
                            $form
                                .find("input[name=utmSource]")
                                .val(utms.source);
                        if ($form.find("input[name=utmMedium]"))
                            $form
                                .find("input[name=utmMedium]")
                                .val(utms.medium);
                        if ($form.find("input[name=utmCampaign]"))
                            $form
                                .find("input[name=utmCampaign]")
                                .val(utms.campaign);
                        if ($form.find("input[name=utmContent]"))
                            $form
                                .find("input[name=utmContent]")
                                .val(utms.content);
                    }
                } catch (error) {
                    console.error(
                        "Failed to get utms from local marketingContext",
                        error
                    );
                }
            });

            //callback when form is submitted
            form.onSuccess(function () {
                setSuccessStatus();
                return false;
            });

            //add-on validation method for email format
            //this is meant to minic the validation method in the magnolia-resources marketo-form component
            form.onValidate(function () {
                const field = form.getFormElem().find("input[type=email]"),
                    value = field.val(),
                    domain = value.toLowerCase().substring(value.indexOf("@")),
                    parts = domain.split("."),
                    freemail = [
                        "@gmail",
                        "@yahoo",
                        "@aol",
                        "@comcast",
                        "@hotmail",
                        "@ymail",
                        "@msn",
                        "@mac",
                        "@sina",
                        "@freemail",
                        "@me",
                        "@icloud",
                        "@live",
                        "@gnail",
                        "@gmsil",
                        "@gmial",
                        "@gamil",
                        "@gail"
                    ];

                // if businessEmailValidation is undefined, we'll assume domain restriction not enforced.
                const freeDomainRestriction = () => {
                    if (businessEmailValidation) {
                        const restrictedDomain = domain.substring(1);
                        // Assume businessEmailValidation comes with {{domain}} tag.
                        const businessEmailValidationErrorMessage =
                            businessEmailValidation.replace(
                                "{{domain}}",
                                restrictedDomain
                            );
                        form.submitable(false);
                        field.focus();
                        form.showErrorMessage(
                            businessEmailValidationErrorMessage,
                            field
                        );
                    } else {
                        form.submitable(true);
                    }
                };

                // Restricted domain validation
                if (
                    freemail.filter((f) => {
                        if (f === "@comcast" && domain === "@comcast.com") {
                            return false;
                        }
                        return parts.indexOf(f) !== -1;
                    }).length === 0
                ) {
                    form.submitable(true);
                } else {
                    freeDomainRestriction();
                }
            });

            //get preferences
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            $.getJSON(mktoConfig.prefsUrl, function (data: any) {
                //set and hide country if it's available
                const $country = $("select#Country");
                if ($country.length && data.locale) {
                    $country.closest(".mktoFormRow").hide();
                    $country.val(data.locale);
                }

                //gdpr checking
                const $gdprInput = $("input[name=form_gmoiGranted]");
                if (
                    $gdprInput.length &&
                    !data.localeRequiresMarketingCommunicationOptIn
                ) {
                    $gdprInput.closest(".mktoFormRow").hide();
                    $gdprInput.prop("checked", true);
                }

                //done checking, show form
                $container.show();
                setFormStatus();
            });
        }
    );
}
